import { inject } from "@angular/core";
import {
  ResolveFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";
import { TranslationLoaderService } from "../services/translation-loader.service";

/**
 * Translation Resolver - Loads and merges view-specific translations before route activation
 *
 * Usage:
 * {
 *   path: 'cases',
 *   component: CaseComponent,
 *   resolve: { translations: translationResolver('cases') }
 * }
 *
 * Flow:
 * 1. Loads view-specific translations from /assets/i18n/{lang}/{viewName}.json
 * 2. Merges with global translations (single source of truth)
 * 3. Updates ngx-translate service with merged translations
 */

/**
 * Resolver with explicit view name(s) parameter
 *
 * Usage (single view):
 * resolve: { translations: translationResolver('cases') }
 *
 * Usage (multiple views):
 * resolve: { translations: translationResolver(['storage-fees', 'cogs']) }
 *
 * @param viewNames - The name(s) of the view/translations file(s) to load (e.g., 'cases', ['storage-fees', 'cogs'])
 * @returns ResolveFn that returns true when translations are loaded and merged
 */
export const translationResolver =
  (viewNames: string | string[]): ResolveFn<boolean> =>
  (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> => {
    const translationLoaderSvc = inject(TranslationLoaderService);
    const viewNamesArray = Array.isArray(viewNames) ? viewNames : [viewNames];

    // Load and merge all view translations sequentially
    const loadPromises = viewNamesArray.map((viewName) =>
      translationLoaderSvc
        .loadAndMergeViewTranslations(viewName)
        .toPromise()
        .catch((error) => {
          console.error(`Failed to load translations for ${viewName}:`, error);
          // Continue loading other translations even if one fails
        }),
    );

    return Promise.all(loadPromises)
      .then(() => true)
      .catch((error) => {
        console.error("Error loading translations:", error);
        return true; // Continue navigation even if translations fail to load
      });
  };
